// src/modules/Admin/pages/ServiceRequestsPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Select,
  Tag,
  Space,
  Typography,
  Card,
  Popconfirm,
  Input,
  Row,
  Col,
  Tooltip,
  DatePicker,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckOutlined,
  CloseOutlined,
  SearchOutlined,
  SyncOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { toastError, toastSuccess } from "../../../utils/toast";
import {
  fetchServiceRequests,
  createServiceRequest,
  updateServiceRequest,
  deleteServiceRequest,
} from "../../../api/serviceRequestsApi";
import { fetchServices } from "../../../api/servicesApi";
import { fetchTeams } from "../../../api/teamApi"; // <-- used for modal lawyers

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;

export default function ServiceRequestsPage() {
  const [requests, setRequests] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(false);

  const [services, setServices] = useState([]);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null); // current row in edit
  const [form] = Form.useForm();

  // Lawyers/Teams for the modal (filtered by the "Requested By" firm)
  const [modalLawyers, setModalLawyers] = useState([]);
  const [loadingModalLawyers, setLoadingModalLawyers] = useState(false);

  // Description preview modal
  const [descModal, setDescModal] = useState({
    open: false,
    text: "",
    title: "",
  });

  // Filters
  const [filterForm] = Form.useForm();
  const defaultFilters = {
    search: "",
    user_id: undefined,
    service_id: undefined,
    status: undefined,
    dateRange: [],
  };
  const [filters, setFilters] = useState(defaultFilters);

  const { id: userId, role } = useMemo(() => {
    try {
      const u = JSON.parse(localStorage.getItem("user")) || {};
      return { id: u.id, role: u.role || "" };
    } catch {
      return { id: null, role: "" };
    }
  }, []);

  const canCreate = role === "admin" || role === "lawyer" || role === "firm";

  // Load all data
  const loadAll = async () => {
    setLoading(true);
    try {
      const [reqs, srv] = await Promise.all([fetchServiceRequests(), fetchServices()]);
      setRequests(reqs);
      setFiltered(reqs);
      // keep only active services
      setServices((srv || []).filter((s) => Number((s?.is_active ?? 0)) === 1));
    } catch (e) {
      toastError(e?.message || "Failed to load service requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const handleResetFilters = () => {
    filterForm.resetFields();
    setFilters(defaultFilters);
  };

  // Distinct requesters (users) for the filter
  const requesterOptions = useMemo(() => {
    const seen = new Set();
    return (requests || [])
      .filter((r) => {
        const key = String(r.user_id);
        if (!r.user_id || seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .map((r) => ({ value: r.user_id, label: r.user_name || `User #${r.user_id}` }));
  }, [requests]);

  // Distinct services for the filter; depends on selected requester
  const serviceOptions = useMemo(() => {
    let base = requests || [];
    if (filters.user_id) {
      base = base.filter((r) => String(r.user_id) === String(filters.user_id));
    }
    const seen = new Set();
    return base
      .filter((r) => {
        const key = String(r.service_id);
        if (!r.service_id || seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .map((r) => ({ value: r.service_id, label: r.service_name || `Service #${r.service_id}` }));
  }, [requests, filters.user_id]);

  // Apply filters
  useEffect(() => {
    let res = [...(requests || [])];

    // search (service_name or user_name)
    if (filters.search) {
      const q = filters.search.toLowerCase();
      res = res.filter(
        (r) =>
          (r.service_name || "").toLowerCase().includes(q) ||
          (r.user_name || "").toLowerCase().includes(q)
      );
    }

    // requester
    if (filters.user_id) {
      res = res.filter((r) => String(r.user_id) === String(filters.user_id));
    }

    // service (depends on requester selection)
    if (filters.service_id) {
      res = res.filter((r) => String(r.service_id) === String(filters.service_id));
    }

    // status
    if (filters.status) {
      res = res.filter(
        (r) => String(r.status || "").toLowerCase() === String(filters.status).toLowerCase()
      );
    }

    // date range
    if (filters.dateRange && filters.dateRange.length === 2) {
      const [start, end] = filters.dateRange;
      const s = dayjs(start).startOf("day");
      const e = dayjs(end).endOf("day");
      res = res.filter((r) => {
        const d = dayjs(r.created_at);
        return d.isValid() && d.isAfter(s.subtract(1, "ms")) && d.isBefore(e.add(1, "ms"));
      });
    }

    setFiltered(res);
  }, [filters, requests]);

  // Live filter change
  const onFilterChange = (_, allValues) => {
    setFilters((prev) => ({ ...prev, ...allValues }));
  };

  // When requester changes, clear service_id in form and state
  const onRequesterChange = (val) => {
    filterForm.setFieldsValue({ service_id: undefined });
    setFilters((prev) => ({ ...prev, user_id: val, service_id: undefined }));
  };

  // ---------- Helpers to handle selected lawyers on Edit ----------
  const toNumArray = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val.map((v) => Number(v)).filter((n) => !Number.isNaN(n));
    if (typeof val === "string") {
      return val
        .split(",")
        .map((s) => Number(String(s).trim()))
        .filter((n) => !Number.isNaN(n));
    }
    return [];
  };

  // returns normalized numeric lawyer id array from a row
  const getRowLawyerIds = (row) => {
    if (!row) return [];
    if (Array.isArray(row.lawyer_ids)) return toNumArray(row.lawyer_ids);
    if (row.lawyer_ids) return toNumArray(row.lawyer_ids); // CSV string from backend
    if (row.lawyer_id) return toNumArray([row.lawyer_id]); // single fallback
    return [];
  };

  const getRowTeamsIds = (row) => {
    if (!row) return [];
    if (Array.isArray(row.team_ids)) return toNumArray(row.team_ids);
    if (row.team_ids) return toNumArray(row.team_ids); // CSV string from backend
    if (row.team_ids) return toNumArray([row.team_ids]); // single fallback
    return [];
  };

  // Load lawyers for a given firm and (optionally) set selected after options are ready
  const loadLawyersForFirm = async (firmUserId, preselectIds = [], preselectTeamIds = []) => {
    setLoadingModalLawyers(true);
    try {
      const all = await fetchTeams({ firm_id: userId }); // returns list of all lawyers; we filter by firm
      const list = (all || []).filter((l) => String(l.firm_id) === String(firmUserId));
      setModalLawyers(list);

      // preselect after options are present
      if (preselectIds.length) {
        const optionIds = list
          .map((l) => Number(l.user_id ?? l.id))
          .filter((n) => !Number.isNaN(n));
        const selected = preselectIds.filter((id) => optionIds.includes(Number(id)));
        form.setFieldsValue({ lawyer_ids: selected });
      }
      if (preselectTeamIds.length) {
        const optionIds = list
          .map((l) => Number(l.user_id ?? l.id))
          .filter((n) => !Number.isNaN(n));
        const selected = preselectTeamIds.filter((id) => optionIds.includes(Number(id)));
        form.setFieldsValue({ team_ids: selected });
      }
    } catch (e) {
      setModalLawyers([]);
      toastError(e?.message || "Failed to load lawyers");
    } finally {
      setLoadingModalLawyers(false);
    }
  };
  // ---------------------------------------------------------------

  // Open Create
  const openCreate = async () => {
    setEditing(null);
    setModalLawyers([]);
    form.resetFields();
    form.setFieldsValue({
      status: "Pending",
      service_id: undefined,
      lawyer_ids: [],
      team_ids: [],
      description: "",
    });

    // If current user is a firm, load that firm's lawyers (no preselects)
    if (role === "firm" && userId) {
      await loadLawyersForFirm(userId);
    }

    setModalOpen(true);
  };

  // Open Edit
  const openEdit = async (row) => {
    setEditing(row);
    setModalLawyers([]);
    form.resetFields();

    // set basic fields first; selections set after options load
    form.setFieldsValue({
      service_id: row.service_id,
      status: row.status || "Pending",
      lawyer_ids: [],
      team_ids: [],
      description: row.description || "",
    });

    // gather preselected IDs from row (supports array/csv/single)
    const preselectIds = getRowLawyerIds(row);
    const preselectTeamIds = getRowTeamsIds(row);

    // Load lawyers for this request's firm (Requested By) and then set selected (both arrays)
    if (row?.user_id) {
      await loadLawyersForFirm(row.user_id, preselectIds, preselectTeamIds);
    }

    setModalOpen(true);
  };

  // Submit (Create/Update)
  const handleSubmit = async (values) => {
    try {
      if (!userId) {
        toastError("User not found. Please login again.");
        return;
      }

      const payload = {
        service_id: values.service_id,
        status: values.status || "Pending",
        description: values.description || "",
        team_ids: Array.isArray(values.team_ids) ? values.team_ids : [],
        lawyer_ids: Array.isArray(values.lawyer_ids) ? values.lawyer_ids : [],
      };

      if (editing) {
        await updateServiceRequest(editing.id, payload);
        toastSuccess("Request updated");
      } else {
        await createServiceRequest({ user_id: userId, ...payload });
        toastSuccess("Request created");
      }

      setModalOpen(false);
      setEditing(null);
      form.resetFields();
      await loadAll();
    } catch (e) {
      toastError(e?.response?.data?.message || e?.message || "Save failed");
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteServiceRequest(id);
      toastSuccess("Request deleted");
      await loadAll();
    } catch (e) {
      toastError(e?.response?.data?.message || "Failed to delete");
    }
  };

  const openDescriptionModal = (row) => {
    setDescModal({
      open: true,
      text: row?.description || "",
      title: row?.service_name ? `Description — ${row.service_name}` : "Description",
    });
  };

  const columns = [
    {
      title: "Service",
      dataIndex: "service_name",
      key: "service_name",
      render: (t) => <Text strong>{t || "-"}</Text>,
      sorter: (a, b) => (a.service_name || "").localeCompare(b.service_name || ""),
    },
    {
      title: "Requested By",
      dataIndex: "user_name",
      key: "user_name",
      sorter: (a, b) => (a.user_name || "").localeCompare(b.user_name || ""),
    },
    {
      title: "Lawyers",
      dataIndex: "lawyer_names",
      key: "lawyers",
      render: (_, row) => {
        const list = Array.isArray(row.lawyer_names_arr)
          ? row.lawyer_names_arr
          : (row.lawyer_names || "")
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean);
        if (!list.length) return "-";
        return (
          <Space size={[4, 4]} wrap>
            {list.map((name, idx) => (
              <Tooltip title={name} key={`${name}-${idx}`}>
                <Tag>{name}</Tag>
              </Tooltip>
            ))}
          </Space>
        );
      },
      sorter: (a, b) => (a.lawyer_names || "").localeCompare(b.lawyer_names || ""),
      ellipsis: true,
    },
    {
      title: "Teams",
      dataIndex: "team_names",
      key: "teams",
      render: (_, row) => {
        const list = Array.isArray(row.team_names_arr)
          ? row.team_names_arr
          : (row.team_names || "")
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean);
        if (!list.length) return "-";
        return (
          <Space size={[4, 4]} wrap>
            {list.map((name, idx) => (
              <Tooltip title={name} key={`${name}-${idx}`}>
                <Tag>{name}</Tag>
              </Tooltip>
            ))}
          </Space>
        );
      },
      sorter: (a, b) => (a.team_names || "").localeCompare(b.team_names || ""),
      ellipsis: true,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: 360,
      render: (text, row) => {
        const desc = (text || "").trim();
        if (!desc) return "-";
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 8, maxWidth: 340 }}>
            <Paragraph
              style={{ margin: 0, wordBreak: "break-word" }}
              ellipsis={{ rows: 2, tooltip: desc }}
            >
              {desc}
            </Paragraph>
            <Tooltip title="View full">
              <Button
                size="small"
                type="text"
                icon={<EyeOutlined />}
                onClick={() => openDescriptionModal(row)}
              />
            </Tooltip>
          </div>
        );
      },
      sorter: (a, b) => (a.description || "").localeCompare(b.description || ""),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (s) => {
        const status = String(s || "pending").toLowerCase();
        let color = "default";
        let icon;
        switch (status) {
          case "accepted":
            color = "success";
            icon = <CheckOutlined />;
            break;
          case "pending":
            color = "warning";
            icon = undefined;
            break;
          case "rejected":
            color = "error";
            icon = <CloseOutlined />;
            break;
          case "resolved":
            color = "processing";
            icon = <CheckOutlined />;
            break;
          default:
            color = "default";
        }
        return (
          <Tag icon={icon} color={color}>
            {s || "Pending"}
          </Tag>
        );
      },
      filters: [
        { text: "Pending", value: "Pending" },
        { text: "Accepted", value: "Accepted" },
        { text: "Rejected", value: "Rejected" },
        { text: "Resolved", value: "Resolved" },
      ],
      onFilter: (value, record) =>
        String(record.status || "").toLowerCase() === String(value).toLowerCase(),
    },
    {
      title: "Created At",
      dataIndex: "created_at",
      key: "created_at",
      render: (d) => (d ? new Date(d).toLocaleString() : "-"),
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 120,
      render: (_, row) => {
        if (role === "lawyer") {
          return null;
        }
        if (role === "firm") {
          return (
            <Space size="small">
              <Popconfirm
                title="Delete this request?"
                okText="Delete"
                placement="left"
                onConfirm={() => handleDelete(row.id)}
              >
                <Button
                  size="small"
                  type="text"
                  icon={<DeleteOutlined style={{ color: "#ff4d4f" }} />}
                  danger
                />
              </Popconfirm>
            </Space>
          );
        }
        return (
          <Space size="small">
            <Button
              size="small"
              type="text"
              icon={<EditOutlined style={{ color: "#1677ff" }} />}
              onClick={() => openEdit(row)}
            />
            <Popconfirm
              title="Delete this request?"
              okText="Delete"
              placement="left"
              onConfirm={() => handleDelete(row.id)}
            >
              <Button
                size="small"
                type="text"
                icon={<DeleteOutlined style={{ color: "#ff4d4f" }} />}
                danger
              />
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <Card
        title={
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Title level={4} style={{ margin: 0 }}>Service Requests</Title>
            {canCreate && role !== "lawyer" && (
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                New Request
              </Button>
            )}
          </div>
        }
        bordered={false}
        style={{ boxShadow: "0 1px 2px rgba(0,0,0,0.03)" }}
      >
        {/* Filters */}
        <Card bordered={false} bodyStyle={{ padding: 16 }} style={{ marginBottom: 16 }}>
          <Form
            form={filterForm}
            layout="vertical"
            onValuesChange={onFilterChange}
            initialValues={filters}
          >
            <Row gutter={[12, 12]} align="bottom" wrap={false}>
              <Col xs={24} md={6}>
                <Form.Item name="search" label="Search">
                  <Input
                    placeholder="Search by service or requester"
                    allowClear
                    prefix={<SearchOutlined />}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={4}>
                <Form.Item name="user_id" label="Requester">
                  <Select
                    allowClear
                    placeholder="Select requester"
                    onChange={onRequesterChange}
                    options={requesterOptions}
                    showSearch
                    optionFilterProp="label"
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={4}>
                <Form.Item name="service_id" label="Service">
                  <Select
                    allowClear
                    placeholder="Select service"
                    options={serviceOptions}
                    showSearch
                    optionFilterProp="label"
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={4}>
                <Form.Item name="status" label="Status">
                  <Select
                    allowClear
                    placeholder="Select status"
                    options={[
                      { value: "Pending", label: "Pending" },
                      { value: "Accepted", label: "Accepted" },
                      { value: "Rejected", label: "Rejected" },
                      { value: "Resolved", label: "Resolved" },
                    ]}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} md={6}>
                <Form.Item name="dateRange" label="Between Dates">
                  <RangePicker style={{ width: "100%" }} />
                </Form.Item>
              </Col>
            </Row>

            <Row justify="end">
              <Col>
                <Button icon={<SyncOutlined />} onClick={handleResetFilters}>
                  Reset
                </Button>
              </Col>
            </Row>
          </Form>
        </Card>

        <Table
          rowKey="id"
          columns={columns}
          dataSource={filtered}
          loading={loading}
          bordered
          scroll={{ x: true }}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            showTotal: (t) => `Total ${t} requests`,
          }}
        />
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        title={<Title level={5} style={{ margin: 0 }}>{editing ? "Edit Request" : "Create Request"}</Title>}
        open={modalOpen}
        onCancel={() => {
          setModalOpen(false);
          setEditing(null);
          form.resetFields();
          setModalLawyers([]);
        }}
        onOk={() => form.submit()}
        okText={editing ? "Update" : "Create"}
        destroyOnClose
        centered
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="service_id"
            label="Service"
            rules={[{ required: true, message: "Please select a service" }]}
          >
            <Select showSearch placeholder="Select a service" optionFilterProp="label">
              {(services || []).map((s) => (
                <Option key={s.id} value={s.id} label={s.name}>
                  {s.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {/* Description textarea */}
          <Form.Item name="description" label="Description">
            <Input.TextArea
              placeholder="Describe what you need…"
              autoSize={{ minRows: 3, maxRows: 6 }}
              showCount
              maxLength={1000}
            />
          </Form.Item>

          {/* Assign Team */}
          <Form.Item name="team_ids" label="Assign Team">
            <Select
              mode="multiple"
              allowClear
              showSearch
              placeholder={
                editing
                  ? loadingModalLawyers
                    ? "Loading team..."
                    : modalLawyers.length
                    ? "Select Team Member(s)"
                    : "No Team for this requester"
                  : role === "firm"
                  ? loadingModalLawyers
                    ? "Loading lawyers..."
                    : modalLawyers.length
                    ? "Select Team Member(s)"
                    : "No Team found for your firm"
                  : "Select Team Member(s)"
              }
              optionFilterProp="label"
              loading={loadingModalLawyers}
            >
              {modalLawyers.map((l) => {
                const id = Number(l.user_id ?? l.id);
                return (
                  <Option key={id} value={id} label={l.name}>
                    {l.name}
                  </Option>
                );
              })}
            </Select>
          </Form.Item>

          {/* Status */}
          {role !== "firm" && (
            <Form.Item name="status" label="Status" initialValue="Pending">
              <Select
                options={[
                  { value: "Pending", label: "Pending" },
                  { value: "Accepted", label: "Accepted" },
                  { value: "Rejected", label: "Rejected" },
                  { value: "Resolved", label: "Resolved" },
                ]}
              />
            </Form.Item>
          )}
        </Form>
      </Modal>

      {/* Full Description Modal */}
      <Modal
        open={descModal.open}
        title={descModal.title || "Description"}
        footer={
          <Button type="primary" onClick={() => setDescModal((s) => ({ ...s, open: false }))}>
            Close
          </Button>
        }
        onCancel={() => setDescModal((s) => ({ ...s, open: false }))}
        width={720}
        centered
      >
        <Paragraph style={{ whiteSpace: "pre-wrap", marginBottom: 0 }}>
          {descModal.text || "-"}
        </Paragraph>
      </Modal>
    </div>
  );
}
